import { api, getHeaders } from '@/api/client';
import { EntityNotFoundError } from '@/api/errors';
import type { ApiResponse } from '@/api/schema/common';
import type { QueryCriteria, ResponseType } from '@/api/types';
import { buildQueryParams, validateResponse } from '@/api/utils';
import type { z } from 'zod';

// ============ TYPE DEFINITIONS ============

/**
 * Type mapping for response types
 * Maps ResponseType to the corresponding array type
 */
export interface ResponseTypeMap<TCore, TFlat, TEx, TInfo> {
  flat: TFlat[];
  hierarchy: TEx[];
  info: TInfo[];
  core: TCore[];
}

/**
 * Configuration for creating CRUD endpoints
 * Requires all Zod schemas for validation
 */
export interface CrudEndpointsConfig<TCore, TFlat, TEx, TInfo> {
  baseUrl: string;
  entityName: string;
  schemas: {
    core: z.ZodType<TCore>;
    flat: z.ZodType<TFlat>;
    ex: z.ZodType<TEx>;
    info: z.ZodType<TInfo>;
    coreArray: z.ZodType<TCore[]>;
    flatArray: z.ZodType<TFlat[]>;
    exArray: z.ZodType<TEx[]>;
    infoArray: z.ZodType<TInfo[]>;
    coreApiResponse: z.ZodType<ApiResponse<TCore>>;
    flatApiResponse: z.ZodType<ApiResponse<TFlat>>;
    exApiResponse: z.ZodType<ApiResponse<TEx>>;
    coreArrayApiResponse: z.ZodType<ApiResponse<TCore[]>>;
    flatArrayApiResponse: z.ZodType<ApiResponse<TFlat[]>>;
    exArrayApiResponse: z.ZodType<ApiResponse<TEx[]>>;
    infoArrayApiResponse: z.ZodType<ApiResponse<TInfo[]>>;
  };
}

/**
 * CRUD endpoints object returned by the factory
 */
export interface CrudEndpoints<TCore, TFlat, TEx, TInfo> {
  // Read operations - type-specific methods for proper inference
  getAllCore: (criteria?: QueryCriteria) => Promise<TCore[]>;
  getAllFlat: (criteria?: QueryCriteria) => Promise<TFlat[]>;
  getAllHierarchy: (criteria?: QueryCriteria) => Promise<TEx[]>;
  getAllInfo: (criteria?: QueryCriteria) => Promise<TInfo[]>;
  getById: (id: string, resType?: ResponseType, usePathSegment?: boolean) => Promise<TEx>;
  getCoreById: (id: string) => Promise<TCore>;

  // Create operations
  createCore: (entity: TCore) => Promise<TCore>;
  create: (entity: TFlat) => Promise<TFlat>;
  createEx: (entity: TEx) => Promise<TEx>;

  // Update operations
  updateCore: (entity: TCore & { Id?: string }) => Promise<TCore>;
  update: (entity: TFlat & { Id?: string }) => Promise<TFlat>;
  updateEx: (entity: TEx) => Promise<TEx>;

  // Delete operation
  delete: (id: string) => Promise<void>;
}

// ============ FACTORY FUNCTION ============

/**
 * Creates CRUD endpoints for an entity
 *
 * @example
 * ```typescript
 * const abstractSparesEndpoints = createCrudEndpoints({
 *   baseUrl: '/pms/api/AbstractSpares',
 *   entityName: 'AbstractSpare',
 *   schemas: { ... }
 * });
 * ```
 */
export function createCrudEndpoints<TCore, TFlat, TEx, TInfo>(
  config: CrudEndpointsConfig<TCore, TFlat, TEx, TInfo>
): CrudEndpoints<TCore, TFlat, TEx, TInfo> {
  const { baseUrl, schemas } = config;

  return {
    // ============ READ OPERATIONS ============

    getAllCore: async (criteria?: QueryCriteria): Promise<TCore[]> => {
      const query = buildQueryParams(criteria, 'core');
      const response = await api.get<ApiResponse<TCore[]>>(`${baseUrl}?${query}`, getHeaders());
      const parsed = validateResponse(schemas.coreArrayApiResponse, response.data);
      return parsed.Data;
    },

    getAllFlat: async (criteria?: QueryCriteria): Promise<TFlat[]> => {
      const query = buildQueryParams(criteria, 'flat');
      const response = await api.get<ApiResponse<TFlat[]>>(`${baseUrl}?${query}`, getHeaders());
      const parsed = validateResponse(schemas.flatArrayApiResponse, response.data);
      return parsed.Data;
    },

    getAllHierarchy: async (criteria?: QueryCriteria): Promise<TEx[]> => {
      const query = buildQueryParams(criteria, 'hierarchy');
      const response = await api.get<ApiResponse<TEx[]>>(`${baseUrl}?${query}`, getHeaders());
      const parsed = validateResponse(schemas.exArrayApiResponse, response.data);
      return parsed.Data;
    },

    getAllInfo: async (criteria?: QueryCriteria): Promise<TInfo[]> => {
      const query = buildQueryParams(criteria, 'info');
      const response = await api.get<ApiResponse<TInfo[]>>(`${baseUrl}?${query}`, getHeaders());
      const parsed = validateResponse(schemas.infoArrayApiResponse, response.data);
      return parsed.Data;
    },

    // Uses query parameter (?Id=...) instead of path segment (/id) so that
    // entity IDs containing slashes (e.g. "VOY/001") are passed correctly
    // to the server. A path-based URL like /api/entities/VOY/001 would break
    // routing because the slash splits the ID into separate path segments.
    // The usePathSegment flag can be set to true for endpoints that support it, but by default we use query parameters for ID-based retrieval to ensure robustness.
    getById: async (id: string, resType: ResponseType = 'hierarchy', usePathSegment = false): Promise<TEx> => {
      if (usePathSegment) {
        const query = buildQueryParams(undefined, resType);
        const response = await api.get<ApiResponse<TEx>>(`${baseUrl}/${id}?${query}`, getHeaders());
        const parsed = validateResponse(schemas.exApiResponse, response.data);
        return parsed.Data;
      }
      const query = buildQueryParams({ Id: id }, resType);
      const response = await api.get<ApiResponse<TEx[]>>(`${baseUrl}?${query}`, getHeaders());
      const parsed = validateResponse(schemas.exArrayApiResponse, response.data);
      const entity = parsed.Data[0];
      if (entity === undefined) {
        throw new EntityNotFoundError(id);
      }
      return entity;
    },

    // Same query-parameter approach as getById — avoids broken URLs for IDs with slashes.
    getCoreById: async (id: string): Promise<TCore> => {
      const query = buildQueryParams({ Id: id }, 'core');
      const response = await api.get<ApiResponse<TCore[]>>(`${baseUrl}?${query}`, getHeaders());
      const parsed = validateResponse(schemas.coreArrayApiResponse, response.data);
      const entity = parsed.Data[0];
      if (entity === undefined) {
        throw new EntityNotFoundError(id);
      }
      return entity;
    },

    // ============ CREATE OPERATIONS ============

    createCore: async (entity: TCore): Promise<TCore> => {
      const response = await api.post<ApiResponse<TCore> | { Id: string }>(baseUrl, entity, getHeaders());

      if ('Id' in response.data && !('Data' in response.data)) {
        return { ...entity, Id: response.data.Id } as TCore;
      }

      const parsed = validateResponse(schemas.coreApiResponse, response.data);
      return parsed.Data;
    },

    create: async (entity: TFlat): Promise<TFlat> => {
      const response = await api.post<ApiResponse<TFlat> | { Id: string }>(baseUrl, entity, getHeaders());

      if ('Id' in response.data && !('Data' in response.data)) {
        return { ...entity, Id: response.data.Id } as TFlat;
      }

      const parsed = validateResponse(schemas.flatApiResponse, response.data);
      return parsed.Data;
    },

    createEx: async (entity: TEx): Promise<TEx> => {
      const response = await api.post<ApiResponse<TEx> | { Id: string }>(`${baseUrl}/ex`, entity, getHeaders());

      if ('Id' in response.data && !('Data' in response.data)) {
        return { ...entity, Id: response.data.Id } as TEx;
      }

      const parsed = validateResponse(schemas.exApiResponse, response.data);
      return parsed.Data;
    },

    // ============ UPDATE OPERATIONS ============

    updateCore: async (entity: TCore & { Id?: string }): Promise<TCore> => {
      if (entity.Id === undefined) {
        throw new Error('Entity Id is required for update');
      }
      const response = await api.patch<ApiResponse<TCore> | Record<string, never>>(
        `${baseUrl}/${entity.Id}`,
        entity,
        getHeaders()
      );

      if (Object.keys(response.data).length === 0 || !('Data' in response.data)) {
        return entity as TCore;
      }

      const parsed = validateResponse(schemas.coreApiResponse, response.data);
      return parsed.Data;
    },

    update: async (entity: TFlat & { Id?: string }): Promise<TFlat> => {
      if (entity.Id === undefined) {
        throw new Error('Entity Id is required for update');
      }
      const response = await api.patch<ApiResponse<TFlat> | Record<string, never>>(
        `${baseUrl}/${entity.Id}`,
        entity,
        getHeaders()
      );

      if (Object.keys(response.data).length === 0 || !('Data' in response.data)) {
        return entity as TFlat;
      }

      const parsed = validateResponse(schemas.flatApiResponse, response.data);
      return parsed.Data;
    },

    updateEx: async (entity: TEx): Promise<TEx> => {
      const response = await api.put<ApiResponse<TEx> | Record<string, never>>(`${baseUrl}/ex`, entity, getHeaders());

      if (Object.keys(response.data).length === 0 || !('Data' in response.data)) {
        return entity;
      }

      const parsed = validateResponse(schemas.exApiResponse, response.data);
      return parsed.Data;
    },

    // ============ DELETE OPERATION ============

    delete: async (id: string): Promise<void> => {
      await api.delete(`${baseUrl}/${id}`, getHeaders());
    }
  };
}
